/* eslint-disable no-bitwise */
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';

export const DAILY_DAY_VIBE_COUNT = 12;
export const DAY_VIBE_EPOCH_DATE = '2026-05-26';

export const DAY_VIBE_TRAITS = [
  'social',
  'calm',
  'explorer',
  'chaos',
  'creative',
  'deep',
  'energetic',
  'thoughtful',
  'aesthetic',
  'grounded',
  'spontaneous',
  'nostalgic',
  'emotionally_open',
  'observant',
  'adventurous',
] as const;

export type DayVibeTrait = typeof DAY_VIBE_TRAITS[number];

export type DayVibeQuestion = {
  id: string;
  category: string;
  question: string;
  options: string[];
  minVisibleOptions: number;
  maxVisibleOptions: number;
  allowMultiSelect: boolean;
  weightTags: DayVibeTrait[];
  moodTags: string[];
  optionTraits?: Array<{
    option: string;
    traits: DayVibeTrait[];
  }>;
  optionTraitWeights?: Array<{
    option: string;
    weights: Partial<Record<DayVibeTrait, number>>;
  }>;
  active: boolean;
};

export type DailyDayVibeQuestion = DayVibeQuestion & {
  visibleOptions: string[];
};

export function hashString(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function mulberry32(seed: number) {
  return () => {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function seededShuffle<T>(items: readonly T[], seed: number) {
  const random = mulberry32(seed);
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }
  return copy;
}

export function getUtcDateKey(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

export function getDayOffset(dateKey: string, epochDate = DAY_VIBE_EPOCH_DATE) {
  const epoch = Date.parse(`${epochDate}T00:00:00.000Z`);
  const current = Date.parse(`${dateKey}T00:00:00.000Z`);
  return Math.max(0, Math.floor((current - epoch) / 86400000));
}

function getVisibleOptionBounds(
  question: Pick<DayVibeQuestion, 'options' | 'minVisibleOptions' | 'maxVisibleOptions'>,
) {
  const optionCount = question.options.length;
  const minVisible = Math.min(optionCount, Math.max(2, question.minVisibleOptions || 2));
  const maxVisible = Math.min(optionCount, Math.max(minVisible, question.maxVisibleOptions || optionCount));
  return { minVisible, maxVisible };
}

function buildOptionCombinations<T>(items: readonly T[], minSize: number, maxSize: number): T[][] {
  const combinations: T[][] = [];

  const visit = (startIndex: number, targetSize: number, selected: T[]) => {
    if (selected.length === targetSize) {
      combinations.push([...selected]);
      return;
    }

    const remainingNeeded = targetSize - selected.length;
    for (let index = startIndex; index <= items.length - remainingNeeded; index += 1) {
      selected.push(items[index]);
      visit(index + 1, targetSize, selected);
      selected.pop();
    }
  };

  for (let size = minSize; size <= maxSize; size += 1) {
    visit(0, size, []);
  }

  return combinations;
}

export function getVisibleOptionsForQuestion(
  question: Pick<DayVibeQuestion, 'id' | 'options' | 'minVisibleOptions' | 'maxVisibleOptions'>,
  dateKey = getUtcDateKey(),
  occurrenceIndex = getDayOffset(dateKey),
) {
  const { minVisible, maxVisible } = getVisibleOptionBounds(question);
  const combinations = buildOptionCombinations(question.options, minVisible, maxVisible);
  if (!combinations.length) return [];

  const combinationCycle = Math.floor(occurrenceIndex / combinations.length);
  const orderedCombinations = seededShuffle(
    combinations,
    hashString(`${question.id}:visible-option-combinations:${combinationCycle}`),
  );
  const selectedCombination = orderedCombinations[occurrenceIndex % orderedCombinations.length];
  return seededShuffle(
    selectedCombination,
    hashString(`${question.id}:visible-option-order:${occurrenceIndex}`),
  );
}

export function getDailyDayVibeQuestions(
  questions: DayVibeQuestion[],
  dateKey = getUtcDateKey(),
): DailyDayVibeQuestion[] {
  const activeQuestions = questions
    .filter((question) => question.active !== false)
    .sort((left, right) => left.id.localeCompare(right.id));

  if (!activeQuestions.length) return [];

  const dayOffset = getDayOffset(dateKey);
  const cycleSize = activeQuestions.length;
  const dailyCount = Math.min(DAILY_DAY_VIBE_COUNT, cycleSize);

  // Each "cycle" is one full shuffled permutation of every active question, sliced
  // into fixed-size, non-overlapping day-blocks (dailyCount each). A day's block
  // always comes from a single cycle's permutation, so it can never straddle two
  // independently-shuffled cycles — which is what previously let the same question
  // appear twice in one day, or reappear again before every question had been
  // asked at least once. Any remainder that doesn't fill a full day-block this
  // cycle (cycleSize % dailyCount) simply isn't used until the next cycle's fresh
  // shuffle, rather than being force-fit into a boundary-crossing day.
  const daysPerCycle = Math.max(1, Math.floor(cycleSize / dailyCount));
  const cycleIndex = Math.floor(dayOffset / daysPerCycle);
  const dayInCycle = dayOffset % daysPerCycle;
  const cyclePool = seededShuffle(activeQuestions, hashString(`day-vibes-cycle:${cycleIndex}`));
  const startIndex = dayInCycle * dailyCount;
  const selectedQuestions = cyclePool.slice(startIndex, startIndex + dailyCount);

  return seededShuffle(selectedQuestions, hashString(`day-vibes-daily-order:${dateKey}`)).map((question) => {
    return {
      ...question,
      visibleOptions: getVisibleOptionsForQuestion(question, dateKey, dayOffset),
    };
  });
}

function asStringArray(value: unknown) {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

export function normalizeDayVibeQuestion(id: string, data: Record<string, unknown>): DayVibeQuestion | null {
  const options = asStringArray(data.options);
  if (!options.length || typeof data.question !== 'string') return null;

  return {
    id: typeof data.id === 'string' ? data.id : id,
    category: typeof data.category === 'string' ? data.category : 'uncategorized',
    question: data.question,
    options,
    minVisibleOptions: typeof data.minVisibleOptions === 'number' ? data.minVisibleOptions : 2,
    maxVisibleOptions: typeof data.maxVisibleOptions === 'number' ? data.maxVisibleOptions : 5,
    allowMultiSelect: data.allowMultiSelect !== false,
    weightTags: asStringArray(data.weightTags).filter((tag): tag is DayVibeTrait =>
      (DAY_VIBE_TRAITS as readonly string[]).includes(tag),
    ),
    moodTags: asStringArray(data.moodTags),
    optionTraits: Array.isArray(data.optionTraits)
      ? data.optionTraits.map((entry) => ({
          option: String((entry as Record<string, unknown>).option || ''),
          traits: asStringArray((entry as Record<string, unknown>).traits).filter((tag): tag is DayVibeTrait =>
            (DAY_VIBE_TRAITS as readonly string[]).includes(tag),
          ),
        })).filter((entry) => entry.option && entry.traits.length)
      : undefined,
    optionTraitWeights: Array.isArray(data.optionTraitWeights)
      ? data.optionTraitWeights.map((entry) => {
          const rawWeights = (entry as Record<string, unknown>).weights as Record<string, unknown> | undefined;
          const weights: Partial<Record<DayVibeTrait, number>> = {};
          Object.entries(rawWeights || {}).forEach(([trait, value]) => {
            if ((DAY_VIBE_TRAITS as readonly string[]).includes(trait) && typeof value === 'number') {
              weights[trait as DayVibeTrait] = value;
            }
          });
          return {
            option: String((entry as Record<string, unknown>).option || ''),
            weights,
          };
        }).filter((entry) => entry.option && Object.keys(entry.weights).length)
      : undefined,
    active: data.active !== false,
  };
}

export async function fetchDailyDayVibeQuestions(dateKey = getUtcDateKey()) {
  const snapshot = await getDocs(
    query(
      collection(firestore, FIRESTORE_COLLECTIONS.dayVibeQuestions),
      where('active', '==', true),
      limit(600),
    ),
  );

  const questions = snapshot.docs
    .map((docSnap) => normalizeDayVibeQuestion(docSnap.id, docSnap.data()))
    .filter((question): question is DayVibeQuestion => Boolean(question));

  return getDailyDayVibeQuestions(questions, dateKey);
}
